import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Select, Tag, Spin, message } from 'antd';
import { X, Image as ImageIcon } from 'react-feather';
import { EditOutlined } from '@ant-design/icons';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { API_ROUTES } from '../../../../../../api/ApiRoutes';
import createPrivateAdminPanelInstance from '../../../../../../api/PrivateAdminPanelInstance';

import styles from './stylesCategoryRow.module.css';
import SortableItem from '../SortableItem/SortableItem';
import ToggleSwitch from '../../../../../CommonComponents/ToggleSwitch/ToggleSwitch';
import ClipPath from '../../../../../../assets/icons/ic_clip-path.svg';

const { Option } = Select;

const TopLeaguesRow = ({
  category,
  setTopLeagueGames,
  order,
  toggleStatus,
  onTogglePin,
  removeGame,
  setRemovingGameData,
  etype,
  EditGameIcon,
  columns,
  changeTopLeagueEntityOrder,
  betAmountType,
  onLeagueExpand,
  expandingLeagues,
  allGames,
}) => {
  const [isExpanded, setIsExpanded] = useState(category.expanded || false);

  const sensors = useSensors(useSensor(PointerSensor));

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'grab',
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Filter out undefined/null games before processing
    const validGames = category.games.filter(game => game && game.game_id);
    const oldIndex = validGames.findIndex((g) => g.game_id === active.id);
    const newIndex = validGames.findIndex((g) => g.game_id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const updatedGames = arrayMove(validGames, oldIndex, newIndex);

    // Update local state first for immediate UI feedback
    setTopLeagueGames((prev) =>
      prev.map((cat) =>
        cat.id === category.id ? { ...cat, games: updatedGames } : cat
      )
    );

    // Call API to persist the reordering
    try {
      // Get the token and create API instance
      const token = JSON.parse(localStorage.getItem("token"));
      const apiInstance = createPrivateAdminPanelInstance(token?.IdToken);

      // Extract league_id from category (should be the sportradar league ID)
      const leagueId = category.league_id;

      // Get ordered event IDs (sportradar match IDs)
      const orderedIds = updatedGames.map(game => game.game_id);

      await apiInstance.post(API_ROUTES.REORDER_TOP_LEAGUE_ENTITIES, {
        item_type: "FEATURED_EVENT",
        league_id: leagueId,
        ordered_ids: orderedIds
      });

      // Show success message
      message.success('Event order updated successfully');
    } catch (error) {
      console.error('Error reordering events:', error);
      message.error('Failed to update event order');

      // Revert to original state on error
      setTopLeagueGames((prev) =>
        prev.map((cat) =>
          cat.id === category.id ? { ...cat, games: validGames } : cat
        )
      );
    }
  };


  return (
    <>
      <tr
        ref={setNodeRef}
        style={style}
        {...attributes}
        className={`${styles.categoryRow} ${isExpanded ? styles.expandedRow : ''}`}
      >
        <td className={styles.dragCell} {...listeners}>
          {/* <div className={styles.dragHandle}>⋮⋮</div> */}
          <img className={styles.dragHandle} src={ClipPath} alt="Drag" />
        </td>
        <td className={styles.orderNumberCell}>
          <span className={styles.orderNumber}>{order}</span>
        </td>
        <td className={styles.iconCell}>
          <div className={styles.iconContainer}>
            {category.icon ? (
              <img src={category.icon} alt={category.ename} className={styles.leagueIcon} />
            ) : (
              <div className={styles.leagueIconplaceholder} title="No icon">
                <ImageIcon size={18} />
              </div>
            )}
            {/* <Button
              type="text"
              icon={<EditOutlined size={12} />}
              className={styles.editIcon}
            /> */}
          </div>
        </td>
        <td className={styles.leagueCell}>
          <div className={styles.leagueInfo}>
            <span className={styles.leagueName}>{category.ename}</span>
            <Tag className={styles.sportTag}>{category.sport}</Tag>
          </div>
        </td>
        {/* <td className={styles.idCell}>
          <span className={styles.leagueId}>{category.id}</span>
        </td> */}
        <td className={styles.eventNameCell}></td>
        <td className={styles.orderCell}></td>
        <td className={styles.orderCell}>
          <Select
            value={category.order}
            onChange={(value) => changeTopLeagueEntityOrder(category, value)}
            className={styles.orderSelect}
          >
            {Array.from({ length: allGames?.length || 20 }, (_, i) => (
              <Option key={i + 1} value={(i + 1).toString()}>
                {i + 1}
              </Option>
            ))}
          </Select>
        </td>
        {/* <td className={styles.statusCell}></td> */}
        <td className={styles.statusCell}>
          <ToggleSwitch
            status={category.is_active}
            eventCode={1}
            updateEventCode={toggleStatus}
            data={category}
            onlyUpdate={true}
          />
        </td>
        <td className={styles.actionCell}>
          <div className={styles.actionButtons}>
            <Button
              type="text"
              icon={<X size={12} />}
              onClick={() => removeGame(category)}
              className={styles.deleteButton}
            />
            <Button
              type="text"
              icon={
                expandingLeagues?.has(category.id) ? (
                  <Spin size="small" />
                ) : isExpanded ? (
                  <DownOutlined size={16} />
                ) : (
                  <UpOutlined size={16} />
                )
              }
              onClick={async () => {
                if (!isExpanded && onLeagueExpand) {
                  await onLeagueExpand(category);
                }
                setIsExpanded(!isExpanded);
              }}
              className={styles.expandButton}
              disabled={expandingLeagues?.has(category.id)}
            />
          </div>
        </td>
        <td className={styles.eventDateCell}></td>
        <td className={styles.status2Cell}></td>
        <td className={styles.countryCell}></td>
        <td className={styles.locationCell}></td>
        <td className={styles.providerCell}></td>
        <td className={styles.betsAmountCell}></td>
        <td className={styles.numberOfBetsCell}></td>
        <td className={styles.preMatchTierCell}></td>
        <td className={styles.liveTierCell}></td>
        <td className={styles.liveTimeDelayCell}></td>
        <td className={styles.suggestedLiveCell}></td>
        <td className={styles.preMatchActivityCell}></td>
        <td className={styles.liveActivityCell}></td>
      </tr>

      {isExpanded && category.games && category.games.length > 0 && (() => {
        const validGames = category.games.filter(game => game && game.game_id);
        if (validGames.length === 0) return null;

        return (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={validGames.map((game) => game.game_id)}
              strategy={verticalListSortingStrategy}
            >
              {validGames?.map((game, index) => (
                <SortableItem
                  key={game.game_id}
                  game={game}
                  index={index}
                  order={order}
                  toggleStatus={toggleStatus}
                  removeGame={removeGame}
                  setRemovingGameData={setRemovingGameData}
                  name={category.ename}
                  etype={etype}
                  EditGameIcon={EditGameIcon}
                  onTogglePin={onTogglePin}
                  columns={columns}
                  betAmountType={betAmountType}
                />
              ))}
            </SortableContext>
          </DndContext>
        );
      })()}
    </>
  );
};

export default TopLeaguesRow;
